import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from './firebase/config';
import './Login.css';
import API_BASE_URL, { API_ENDPOINTS } from './config/api';

/* ── Steps:
   email         → single email field, Google button
   new-password  → new user: create password
   returning     → existing user: sign in
   forgot        → reset password form
   forgot-sent   → reset email confirmation
   registered    → new account welcome screen (#7)
─────────────────────────────────────────────── */

const GoogleIcon = () => (
  <svg className="google-icon" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function Login({ onLogin }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingLogin, setPendingLogin] = useState(null);

  const clearErrors = () => { setError(''); setFieldErrors({}); };

  const goBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    clearErrors();
  };

  /* Fetch profile + intake after Firebase auth succeeds */
  const fetchUserData = async (firebaseUser) => {
    const token = await firebaseUser.getIdToken();
    const [profileRes, intakeRes] = await Promise.all([
      fetch(API_ENDPOINTS.AUTH_PROFILE, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(API_ENDPOINTS.INTAKE_STATUS, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (!profileRes.ok) {
      await auth.signOut();
      throw new Error('Your account could not be found. Please contact your coach.');
    }
    const userData = (await profileRes.json()).user;
    const intakeData = intakeRes.ok ? await intakeRes.json() : { hasCompletedIntake: false };
    return {
      email: firebaseUser.email,
      name: userData.firstName && userData.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : firebaseUser.displayName || 'User',
      uid: firebaseUser.uid,
      userData,
      hasCompletedIntake: intakeData.hasCompletedIntake,
      hasSignedContract: userData.hasSignedContract || false,
      hasPaid: userData.hasPaid || false,
      paymentMethod: userData.paymentMethod || null,
      isAdmin: userData.isAdmin || false,
      token,
    };
  };

  /* ── Step 1: email check ── */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearErrors();
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!data.allowed) {
        setError(data.message || 'This email is not registered. Contact your coach to get started.');
        return;
      }
      setFirstName(data.firstName || '');
      setStep(data.status === 'new' ? 'new-password' : 'returning');
    } catch {
      setError('Unable to verify your email. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2a: sign in (returning user) ── */
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearErrors();
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const loginData = await fetchUserData(cred.user);
      onLogin(loginData);
    } catch (err) {
      const c = err.code;
      if (c === 'auth/wrong-password' || c === 'auth/invalid-credential' || c === 'auth/invalid-login-credentials') {
        setError('Incorrect password. Please try again, or use "Forgot password" to reset it.');
      } else if (c === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please reset your password or try again later.');
      } else if (c === 'auth/user-not-found') {
        setError('No account found for this email. Please contact your coach.');
      } else {
        setError(err.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2b: create account (new user) ── */
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    const errs = {};
    if (password.length < 6) errs.password = 'Must be at least 6 characters.';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setLoading(true);
    clearErrors();
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const token = await cred.user.getIdToken();

      const activateRes = await fetch(`${API_BASE_URL}/api/auth/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!activateRes.ok) {
        const d = await activateRes.json();
        throw new Error(d.message || 'Account activation failed. Please contact your coach.');
      }

      const loginData = await fetchUserData(cred.user);
      setPendingLogin(loginData);
      setStep('registered');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists for this email. Taking you to sign in…');
        setTimeout(() => { clearErrors(); setStep('returning'); }, 1800);
      } else {
        setError(err.message || 'Account creation failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Google sign-in / sign-up ── */
  const handleGoogle = async () => {
    setLoading(true);
    clearErrors();
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const token = await result.user.getIdToken();

      const profileRes = await fetch(API_ENDPOINTS.AUTH_PROFILE, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (profileRes.ok) {
        const loginData = await fetchUserData(result.user);
        onLogin(loginData);
        return;
      }

      /* New Google user — try activation */
      const activateRes = await fetch(`${API_BASE_URL}/api/auth/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!activateRes.ok) {
        await auth.signOut();
        setError(
          'Your Google account is not registered with NextLeader. ' +
          'Use the email your coach enrolled you with, or contact your coach to get started.'
        );
        return;
      }

      const loginData = await fetchUserData(result.user);
      setFirstName(loginData.userData?.firstName || '');
      setPendingLogin(loginData);
      setStep('registered');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Google sign-in failed. Please try again or use email and password.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot password ── */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearErrors();
    try {
      await sendPasswordResetEmail(auth, email);
      setStep('forgot-sent');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please contact your coach.');
      } else {
        setError('Could not send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Render ── */
  const renderStep = () => {
    switch (step) {

      case 'email':
        return (
          <>
            <div className="login-header">
              <h2>Sign In</h2>
              <p>Enter the email your coach enrolled you with</p>
            </div>

            <button className="google-btn" onClick={handleGoogle} disabled={loading} type="button">
              <GoogleIcon />
              {loading ? 'Signing in…' : 'Continue with Google'}
            </button>

            <div className="divider"><span>or</span></div>

            <form onSubmit={handleEmailSubmit}>
              {error && <div className="form-error" role="alert">{error}</div>}
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearErrors(); }}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <button type="submit" className="email-btn" disabled={loading || !email.trim()}>
                {loading ? 'Checking…' : 'Continue →'}
              </button>
            </form>
          </>
        );

      case 'returning':
        return (
          <>
            <button className="login-back-btn" onClick={goBack} type="button">← Back</button>
            <div className="login-welcome">
              <p className="login-welcome-name">Welcome back, {firstName}.</p>
              <p className="login-welcome-email">{email}</p>
            </div>

            <form onSubmit={handleSignIn}>
              {error && <div className="form-error" role="alert">{error}</div>}
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearErrors(); }}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  autoFocus
                />
                <div className="forgot-password">
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={() => { clearErrors(); setStep('forgot'); }}
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
              <button type="submit" className="email-btn" disabled={loading || !password}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          </>
        );

      case 'new-password':
        return (
          <>
            <button className="login-back-btn" onClick={goBack} type="button">← Back</button>
            <div className="login-welcome">
              <p className="login-welcome-name">Hi, {firstName}!</p>
              <p className="login-welcome-sub">Your coach has enrolled you with <strong>{email}</strong>. Create a password to access your portal.</p>
            </div>

            <form onSubmit={handleCreateAccount}>
              {error && <div className="form-error" role="alert">{error}</div>}
              <div className="form-group">
                <label htmlFor="password">Create password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors({}); }}
                  placeholder="At least 6 characters"
                  required
                  minLength="6"
                  autoComplete="new-password"
                  autoFocus
                />
                {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors({}); }}
                  placeholder="Re-enter your password"
                  required
                  autoComplete="new-password"
                />
                {fieldErrors.confirmPassword && (
                  <span className="field-error">{fieldErrors.confirmPassword}</span>
                )}
              </div>
              <button
                type="submit"
                className="email-btn"
                disabled={loading || !password || !confirmPassword}
              >
                {loading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          </>
        );

      case 'forgot':
        return (
          <>
            <button className="login-back-btn" onClick={() => { clearErrors(); setStep('returning'); }} type="button">
              ← Back to Sign In
            </button>
            <div className="login-welcome">
              <p className="login-welcome-name">Reset your password</p>
              <p className="login-welcome-sub">
                We'll send a reset link to <strong>{email}</strong>.
              </p>
            </div>

            <form onSubmit={handleForgotPassword}>
              {error && <div className="form-error" role="alert">{error}</div>}
              <button type="submit" className="email-btn" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Email'}
              </button>
            </form>
          </>
        );

      case 'forgot-sent':
        return (
          <div className="login-confirmation">
            <div className="login-confirmation-icon login-confirmation-icon--mail">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <p className="login-confirmation-title">Check your inbox</p>
            <p className="login-confirmation-sub">
              A password reset link has been sent to <strong>{email}</strong>.
              Check your spam folder if it doesn't arrive within a minute.
            </p>
            <button
              type="button"
              className="email-btn"
              onClick={() => { clearErrors(); setStep('returning'); }}
            >
              Back to Sign In
            </button>
          </div>
        );

      case 'registered':
        return (
          <div className="login-confirmation">
            <div className="login-confirmation-icon login-confirmation-icon--check">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p className="login-confirmation-title">Welcome to NextLeader, {firstName}!</p>
            <p className="login-confirmation-sub">
              Your account is ready. Your first step is to review and sign your coaching agreement — it only takes a minute.
            </p>
            <button
              type="button"
              className="email-btn"
              onClick={() => onLogin(pendingLogin)}
            >
              Enter My Portal →
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="login-page">
      {/* Left brand panel */}
      <div className="login-brand" aria-hidden="true">
        <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
          <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="login-brand-logo" />
        </a>
        <div className="login-brand-body">
          <p className="login-brand-eyebrow">Executive Coaching Portal</p>
          <h1 className="login-brand-headline">Coaching Built for Life Sciences Leaders</h1>
          <p className="login-brand-sub">
            Partner with a coach who understands the science, the business, and the pressure of leading in biotech and pharma.
          </p>
          <div className="login-brand-pills">
            <div className="login-brand-pill">
              <span className="login-brand-pill-dot" />Life Sciences Industry Expertise
            </div>
            <div className="login-brand-pill">
              <span className="login-brand-pill-dot" />Personalized Development Plans
            </div>
            <div className="login-brand-pill">
              <span className="login-brand-pill-dot" />Confidential 1:1 Coaching
            </div>
          </div>
        </div>
        <p className="login-brand-footer">
          &copy; {new Date().getFullYear()} NextLeader Coaching. All rights reserved.
        </p>
      </div>

      {/* Right form panel */}
      <div className="login-form-panel">
        <div className="login-card">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}

export default Login;
